/* eslint-disable no-console */
// Global toast manager for non-React contexts
// This allows us to show toasts from API client and other non-React code

type ToastData = {
  title?: string;
  description?: string;
  content?: string;
  state: 'error' | 'success';
};

class ToastManager {
  private toastFunction: ((data: ToastData) => void) | null = null;

  // Register the toast function from a React component
  registerToastFunction(toastFn: (data: ToastData) => void) {
    this.toastFunction = toastFn;
  }

  // Show a toast (can be called from anywhere)
  showToast(data: ToastData) {
    if (this.toastFunction) {
      this.toastFunction(data);
    } else {
      // Fallback to console if no toast function is registered
      console.log(`Toast: ${data.title} - ${data.description || data.content}`);
    }
  }

  // Convenience methods
  showError(title: string, description?: string) {
    this.showToast({
      title,
      description,
      state: 'error'
    });
  }

  showSuccess(title: string, description?: string) {
    this.showToast({
      title,
      description,
      state: 'success'
    });
  }

  // Session expired toast with internationalization and countdown
  showSessionExpired() {
    // Get current locale from URL
    const getCurrentLocale = () => {
      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        const localeMatch = path.match(/^\/([a-z]{2})/);
        return localeMatch ? localeMatch[1] : 'en';
      }
      return 'en';
    };

    const locale = getCurrentLocale();

    // Default English messages
    let title = 'Session Expired';
    let description = 'Your session has expired. Please sign in again.';

    // Override with localized messages based on locale
    switch (locale) {
      case 'no':
        title = 'Sesjon utløpt';
        description = 'Din sesjon har utløpt. Vennligst logg inn igjen.';
        break;
      case 'sv':
        title = 'Sessionen har löpt ut';
        description = 'Din session har löpt ut. Vänligen logga in igen.';
        break;
      case 'da':
        title = 'Session udløbet';
        description = 'Din session er udløbet. Log venligst ind igen.';
        break;
      default:
        // English (default)
        break;
    }

    // Store the session expired message in localStorage to show on sign-in page
    if (typeof window !== 'undefined') {
      localStorage.setItem(
        'sessionExpiredMessage',
        JSON.stringify({title, description})
      );
    }

    // Show toast immediately
    this.showError(title, description);
  }

  // Check for and show stored session expired message
  checkAndShowSessionExpiredMessage() {
    if (typeof window !== 'undefined') {
      const storedMessage = localStorage.getItem('sessionExpiredMessage');
      if (storedMessage) {
        try {
          const {title, description} = JSON.parse(storedMessage);
          this.showError(title, description);
          // Remove the message after showing it
          localStorage.removeItem('sessionExpiredMessage');
        } catch (error) {
          console.error(
            'Failed to parse stored session expired message:',
            error
          );
          localStorage.removeItem('sessionExpiredMessage');
        }
      }
    }
  }
}

// Export singleton instance
export const toastManager = new ToastManager();
